import { computed, ref } from 'vue'
import {
  DocumentRoute,
  DocumentRouteStage,
  DocumentRouteStageParticipant,
} from '@/utils/types/documentRoute'
import {
  documentActiveRoute,
  documentRouteRun,
  documentRouteStageDelete,
  documentRouteStageParticipantCreate,
  documentRouteStageParticipantDelete,
  documentRouteStageParticipantSetResult,
  documentSignatureCreate,
  documentSignatureList,
} from '@/requests/document'
import { openDialog } from '@/dialog'
import { handleAsync, setSnackbar } from 'best-modules/plugins'
import { useStore } from '@/plugins'
import { DocumentContext } from '../types'
import { sortStages } from '@/components/doc-type-routes/helpers'
import { SignedDocument } from '@/dialog/components/sign-document/types'
import { DocumentRouteSignature } from '@/utils/types/document'

const ROUTE_SIGNATURE_CONTEXT_TYPE_ID = 1

export function useDocumentRoute({
  documentId,
  documentSource,
  getDocument,
}: DocumentContext) {
  const store = useStore()
  const userId = computed(() => store.state.user.userData?.id)
  const activeRoute = ref<DocumentRoute | null>(null)
  const signatures = ref<DocumentRouteSignature[]>([])

  const getDocumentSignatureList = () => {
    return documentSignatureList(documentId.value).then(res => {
      signatures.value = res
    })
  }

  const addDocumentSignatures = (
    createdSignatures: DocumentRouteSignature[]
  ) => {
    signatures.value = [...signatures.value, ...createdSignatures]
  }

  const showSignatures = () => {
    openDialog({
      name: 'DocumentRouteSignatures',
      dialogData: {
        signatures: signatures.value,
      },
      params: {
        hideActionButtons: true,
      },
    })
  }

  const getActiveRoute = () => {
    return documentActiveRoute(documentId.value).then(res => {
      activeRoute.value = res ? { ...res, stages: res.stages ?? [] } : null
    })
  }

  const stageCreate = () => {
    if (!activeRoute.value) return
    openDialog({
      name: 'DocTypeRouteStage',
      action: 'create',
      dialogData: {
        stage: {
          docTypeDocumentRouteId: activeRoute.value.id,
          sequence: activeRoute.value.stages.length + 1,
        },
        maxSequence: activeRoute.value.stages.length + 1,
        resource: 'document',
      },
      params: {
        cb: stage => {
          if (activeRoute.value) {
            activeRoute.value.stages.push(stage)
          }
        },
      },
    })
  }

  const stageUpdate = (stage: DocumentRouteStage) => {
    if (!activeRoute.value) return
    openDialog({
      name: 'DocTypeRouteStage',
      action: 'update',
      dialogData: {
        stage: stage,
        maxSequence: activeRoute.value.stages.length + 1,
        resource: 'document',
      },
      params: {
        cb: updatedStage => {
          if (activeRoute.value) {
            activeRoute.value.stages = activeRoute.value.stages.map(s =>
              s.id === updatedStage.id ? updatedStage : s
            )
          }
        },
      },
    })
  }

  const stageDelete = (stageId: number) => {
    return handleAsync(`stageDelete_${stageId}`, () => {
      return documentRouteStageDelete(stageId).then(() => {
        if (activeRoute.value) {
          activeRoute.value.stages = activeRoute.value.stages.filter(
            s => s.id !== stageId
          )
        }
      })
    })
  }

  const stageParticipantCreate = (stageId: number, userId: number) => {
    return documentRouteStageParticipantCreate({
      docTypeDocumentRouteStageId: stageId,
      userId,
    }).then(res => {
      const stage = activeRoute.value?.stages.find(s => s.id === stageId)
      stage?.participants.push(res)
      setSnackbar({ text: 'Учасника успішно додано' })
    })
  }

  const stageParticipantDelete = (stageId: number, participantId: number) => {
    return documentRouteStageParticipantDelete(participantId).then(() => {
      const stage = activeRoute.value?.stages.find(s => s.id === stageId)
      if (stage) {
        stage.participants = stage.participants.filter(
          s => s.id !== participantId
        )
      }
    })
  }

  const currentStage = computed<DocumentRouteStage | null>(() => {
    if (!activeRoute.value?.stages) return null
    return (
      sortStages(activeRoute.value.stages).find(s => {
        return (s.startedAt && s.participants.some(p => !p.resultId)) || null
      }) ?? null
    )
  })

  const currentUserParticipant = computed<DocumentRouteStageParticipant | null>(
    () => {
      let res: DocumentRouteStageParticipant | null = null
      for (const stage of activeRoute.value?.stages || []) {
        res = stage?.participants.find(p => p.userId === userId.value) || null
        if (res) {
          break
        }
      }
      return res
    }
  )

  const routeRun = () => {
    if (
      activeRoute.value &&
      ['draft', 'revision'].includes(documentSource.status) &&
      documentSource.createdBy === userId.value
    ) {
      return documentRouteRun(activeRoute.value.id).then(res => {
        activeRoute.value = res
        return getDocument()
      })
    }
  }

  const handleParticipantActionError = (e: any) => {
    const msg =
      e?.response?.data?.errors?.message?.[0] ||
      e?.response?.data?.message ||
      e?.message ||
      'Помилка виконання дії'
    setSnackbar({ text: msg, color: 'error' })
  }

  const updateParticipantResult = (
    currentParticipant: DocumentRouteStageParticipant,
    resultId: number
  ) => {
    return documentRouteStageParticipantSetResult(
      [currentParticipant.id],
      resultId
    ).then(() => {
      getDocument()
      return getActiveRoute()
    })
  }

  const currentPendingParticipant =
    computed<DocumentRouteStageParticipant | null>(() => {
      if (!currentStage.value) return null
      return (
        currentStage.value.participants.find(
          p => p.userId === userId.value && !p.resultId
        ) || null
      )
    })

  const hasParticipantSignature = computed<boolean>(() => {
    const currentParticipant = currentPendingParticipant.value
    if (!currentParticipant) return false
    return signatures.value.some(signature => {
      return (
        Number(signature.signatureContextTypeId) ===
          ROUTE_SIGNATURE_CONTEXT_TYPE_ID &&
        Number(signature.signatureContextId) === currentParticipant.id
      )
    })
  })

  const createParticipantSignatures = (
    currentParticipant: DocumentRouteStageParticipant,
    signatures: SignedDocument[]
  ) => {
    if (!signatures.length) {
      return Promise.reject(new Error('Документ не було підписано'))
    }

    return documentSignatureCreate(
      signatures.map(signature => {
        return {
          docTypeDocumentId: documentId.value,
          documentSigned: signature.documentSigned,
          signingDate: signature.signingDate,
          fullCertificate: signature.fullCertificate,
          signatureContextTypeId: ROUTE_SIGNATURE_CONTEXT_TYPE_ID,
          signatureContextId: currentParticipant.id,
        }
      })
    ).then(createdSignatures => {
      addDocumentSignatures(createdSignatures)
      return createdSignatures
    })
  }

  const signAndSetParticipantResult = async (
    currentParticipant: DocumentRouteStageParticipant,
    resultId: number
  ) => {
    if (!documentSource.document?.length) {
      return setSnackbar({
        text: 'Документ не містить файлів для підписання',
        color: 'warning',
      })
    }

    if (hasParticipantSignature.value) {
      return updateParticipantResult(currentParticipant, resultId).catch(
        handleParticipantActionError
      )
    }

    return openDialog({
      name: 'SignDocument',
      dialogData: {
        documents: documentSource.document.map(d => {
          return {
            document: d,
            uploadFilesParams: {
              mainFolder: 'document',
              nameFolder: 'signature',
              prefixFolder: documentSource.uuid,
            },
          }
        }),
      },
      params: {
        hideActionButtons: true,
        cb: async signatures => {
          if (!Array.isArray(signatures)) return

          try {
            await createParticipantSignatures(currentParticipant, signatures)
            await updateParticipantResult(currentParticipant, resultId)
            setSnackbar({ text: 'Документ успішно підписано' })
          } catch (e) {
            handleParticipantActionError(e)
            throw e
          }
        },
      },
    })
  }

  const isEcpRequired = computed<boolean>(() => {
    if (currentStage.value?.typeId === 1) {
      return documentSource.definition?.isApprovalEcpRequired
    }
    if (currentStage.value?.typeId === 2) {
      return documentSource.definition?.isSigningEcpRequired
    }

    return false
  })

  const agree = (resultId: number) => {
    if (currentStage.value) {
      if (currentPendingParticipant.value) {
        if (resultId === 1 && isEcpRequired.value) {
          return signAndSetParticipantResult(
            currentPendingParticipant.value,
            resultId
          ).catch(handleParticipantActionError)
        }

        return updateParticipantResult(
          currentPendingParticipant.value,
          resultId
        ).catch(handleParticipantActionError)
      } else {
        setSnackbar({
          text: 'Ви не маєте права на візування в поточній стадії',
        })
      }
    }
  }

  return {
    activeRoute,
    signatures,
    getActiveRoute,
    getDocumentSignatureList,
    addDocumentSignatures,
    showSignatures,
    stageCreate,
    stageUpdate,
    stageDelete,
    stageParticipantCreate,
    stageParticipantDelete,
    currentStage,
    currentUserParticipant,
    hasParticipantSignature,
    isEcpRequired,
    routeRun,
    agree,
  }
}
